import { uniq } from "./nativeUtils";
import config from "../config";
import { FilterOptionEnum, ServiceEnum, UNCATEGORIZED_SKETCHUP_HELP_AGGREGATION, } from "../models/dataModel";
import buildRequestFilter from "./buildRequestFilter";
import { normalizeSearchTerm } from "./functions";
const FIELDS_TO_SEARCH = [
    { name: "text_title", boost: 3 },
    { name: "sub_title", boost: 2 },
    { name: "keywords", boost: 1 },
    { name: "body", boost: 1 },
    { name: "teaser", boost: 1 },
    { name: "taxonomy_names", boost: 1 },
];
function buildFrom(current, resultsPerPage) {
    if (!current || !resultsPerPage)
        return;
    return (current - 1) * resultsPerPage;
}
function buildSort(sortDirection, sortField) {
    if (sortDirection && sortField) {
        return [{ [`${sortField}`]: sortDirection }];
    }
}
function buildOrMatch(searchTerm) {
    let match = [];
    if (searchTerm) {
        match = FIELDS_TO_SEARCH.map((field) => {
            return {
                match: {
                    [field.name]: {
                        query: searchTerm,
                        boost: field.boost,
                    },
                },
            };
        });
    }
    return match;
}
function buildAndMatch(searchTerm) {
    let match = [];
    if (searchTerm) {
        match = FIELDS_TO_SEARCH.map((field) => {
            return {
                match: {
                    [field.name]: {
                        query: searchTerm,
                        operator: "and",
                        boost: field.boost,
                    },
                },
            };
        });
    }
    return match;
}
function buildMatchPhrase(searchTerm) {
    let match = [];
    if (searchTerm) {
        match = FIELDS_TO_SEARCH.map((field) => {
            return {
                match_phrase: {
                    [field.name]: {
                        query: searchTerm,
                        boost: field.boost * 2,
                    },
                },
            };
        });
    }
    return match;
}
function buildIfSearchTerm(searchTerm) {
    return [...buildOrMatch(searchTerm), ...buildAndMatch(searchTerm), ...buildMatchPhrase(searchTerm)];
}
function buildShould(searchTerm) {
    searchTerm = normalizeSearchTerm(searchTerm ?? "");
    if (searchTerm === "*" || searchTerm === "%2A") {
        searchTerm = undefined;
    }
    return searchTerm ? buildIfSearchTerm(searchTerm) : [{ match_all: {} }];
}
function buildIndicesBoost(index, selectedService) {
    const indices = index.split(",");
    if (indices.length > 1) {
        const selected = indices.find((index) => {
            return index.includes(selectedService);
        });
        if (selected)
            return [{ [selected]: 5 }];
    }
}
/*

  Converts current application state to an Elasticsearch request.

  When implementing an onSearch Handler in Search UI, the handler needs to take the
  current state of the application and convert it to an API request.

  For instance, there is a "current" property in the application state that you receive
  in this handler. The "current" property represents the current page in pagination. This
  method converts our "current" property to Elasticsearch's "from" parameter.

  This "current" property is a "page" offset, while Elasticsearch's "from" parameter
  is a "item" offset. In other words, for a set of 100 results and a page size
  of 10, if our "current" value is "4", then the equivalent Elasticsearch "from" value
  would be "40". This method does that conversion.

  We then do similar things for searchTerm, filters, sort, etc.
*/
export default async function buildRequest(index, service, state, uiState) {
    const { current, filters, resultsPerPage, searchTerm, sortDirection, sortField } = state;
    const sort = buildSort(sortDirection, sortField);
    const should = buildShould(searchTerm);
    const indices_boost = buildIndicesBoost(index, service);
    const size = resultsPerPage;
    const from = buildFrom(current, resultsPerPage);
    const filter = buildRequestFilter(service, uiState, filters);
    const resolveQuery = async () => {
        return {
            bool: {
                should: should,
                ...(filter && { filter }),
                minimum_should_match: 1,
            },
        };
    };
    const resolveAggregations = () => {
        const aggs = {};
        // Determine the source (global or service-specific) for aggregations
        const source = uiState.inGlobalSearchMode() ? config.global : config.services[service];
        // Include aggregations for all available filters in the source
        source.filters.forEach((fieldName) => {
            Object.assign(aggs, {
                [fieldName]: { terms: { field: fieldName, size: 500 } },
            });
        });
        if (source.filters.includes(FilterOptionEnum.CATEGORY)) {
            aggs[UNCATEGORIZED_SKETCHUP_HELP_AGGREGATION] = {
                filter: {
                    bool: {
                        filter: [{ wildcard: { _index: `${ServiceEnum.SKETCHUP_HELP}_*` } }],
                        must_not: [{ exists: { field: FilterOptionEnum.CATEGORY } }],
                    },
                },
            };
        }
        const openCategory = uiState.getOpenCategory();
        // If a category is open, include its filters in the aggregations
        if (openCategory) {
            config.services[openCategory].filters.forEach((fieldName) => {
                Object.assign(aggs, {
                    [fieldName]: { terms: { field: fieldName, size: 500 } },
                });
            });
        }
        return aggs;
    };
    /**
     * Resolves the source fields for the request
     * They determine which fields are included in the search results
     *
     * @returns list of source fields, with duplicates removed
     */
    function resolveSourceFields() {
        // Determine the source (global or service-specific)
        const source = uiState.inGlobalSearchMode() ? config.global : config.services[service];
        let sourceFields = source.resultFields;
        // If a category is open, include its result fields as well
        const openCategory = uiState.getOpenCategory();
        if (openCategory) {
            sourceFields = sourceFields.concat(config.services[openCategory].resultFields);
        }
        return uniq(sourceFields);
    }
    const body = {
        track_total_hits: true,
        // Static query Configuration
        // --------------------------
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-highlighting.html
        highlight: {
            fragment_size: 200,
            number_of_fragments: 1,
            fields: {
                title: {},
                teaser: {},
            },
        },
        //https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-source-filtering.html#search-request-source-filtering
        _source: resolveSourceFields(),
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.11/search-aggregations.html
        aggs: resolveAggregations(),
        // Dynamic values based on current Search UI state
        // --------------------------
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/full-text-queries.html
        query: await resolveQuery(),
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.0/search-suggesters.html
        // suggest: {
        //   suggestion: {
        //     text: searchTerm,
        //     term: {
        //       field: "title",
        //     },
        //   },
        // },
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-search.html
        ...(indices_boost && { indices_boost }),
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-sort.html
        ...(sort && { sort }),
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-from-size.html
        ...(size && { size }),
        ...(from && { from }),
    };
    return body;
}
